import { createContext, useContext, useState, useEffect } from 'react';
import { getAdminMe, logoutAdmin, clearAdminSession } from '@/api/client';
import type { AdminUser } from '@/api/client';

interface AuthContextType {
  user: AdminUser | null;
  loading: boolean;
  setUser: (user: AdminUser | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  setUser: () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function init() {
      // Only validate admin session on admin routes (not public pages)
      const isAdminRoute =
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/admin');
      const isLoginPage =
        typeof window !== 'undefined' &&
        window.location.pathname === '/admin/login';

      if (!isAdminRoute || isLoginPage) {
        setLoading(false);
        return;
      }

      const me = await getAdminMe();
      setUser(me);
      setLoading(false);
    }
    init();
  }, []);

  const logout = () => {
    // Fire logout request in the background without blocking the UI.
    // Prevents infinite spinner if the API is slow or unreachable.
    logoutAdmin().catch(() => {});
    clearAdminSession();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}